<script setup>
import { convertToInr } from "@/services/common-services";
import { useUserStore } from "@/store/user-store";
import { computed, ref } from "vue";
import ManageLiabilityDialog from "@/components/dashboard/ManageLiabilityDialog.vue";
import ManageIncomeDialog from "@/components/dashboard/ManageIncomeDialog.vue";
import ManageExpenseDialog from "@/components/dashboard/ManageExpenseDialog.vue";
import AnLoader from '@/components/common/AnLoader.vue';

const store = useUserStore();

const data = computed(() => store.financialStatement);
const assetData = computed(() => data.value?.customerAssets || []);
const liabilityData = computed(() => data.value?.customerLiabilities || []);
const expenseData = computed(() => data.value?.customerExpenses || []);
const incomeData = computed(() => data.value?.customerIncome || []);
const insuranceData = computed(() => data.value?.insuranceDetails || []);

// Filter out zero values
const filteredAssetData = computed(() =>
  assetData.value.filter(item => item.assetValue > 0)
);
const filteredLiabilityData = computed(() =>
  liabilityData.value.filter(item => item.amtOutstanding > 0)
);
const filteredExpenseData = computed(() =>
  expenseData.value.filter(item => item.expAmt > 0)
);
const filteredIncomeData = computed(() =>
  incomeData.value.filter(item => item.incomeAmt > 0)
);

const totalExpense = computed(() =>
  filteredExpenseData.value.reduce((total, item) => total + item.expAmt, 0)
);
const totalIncome = computed(() =>
  filteredIncomeData.value.reduce((total, item) => total + item.incomeAmt, 0)
);
const totalAsset = computed(() =>
  filteredAssetData.value.reduce((total, item) => total + item.assetValue, 0)
);
const totalLiability = computed(() =>
  filteredLiabilityData.value.reduce((total, item) => total + item.amtOutstanding, 0)
);

// Group income by category
const groupedIncome = computed(() => {
  const groups = {};
  incomeData.value.forEach(item => {
    const category = item.incomeHd || 'Other Income';
    if (!groups[category]) {
      groups[category] = [];
    }
    groups[category].push(item);
  });
  return groups;
});

// Group expenses by category
const groupedExpenses = computed(() => {
  const groups = {};
  expenseData.value.forEach(item => {
    const category = item.expHead || 'Other Expenses';
    if (!groups[category]) {
      groups[category] = [];
    }
    groups[category].push(item);
  });
  return groups;
});

// Calculate group totals
const groupTotals = computed(() => {
  const totals = { income: {}, expenses: {} };

  Object.keys(groupedIncome.value).forEach(key => {
    totals.income[key] = groupedIncome.value[key].reduce((sum, item) => sum + item.incomeAmt, 0);
  });

  Object.keys(groupedExpenses.value).forEach(key => {
    totals.expenses[key] = groupedExpenses.value[key].reduce((sum, item) => sum + item.expAmt, 0);
  });

  return totals;
});

const showLiabilityDialog = ref(false);
const showIncomeDialog = ref(false);
const showExpenseDialog = ref(false);
const isLoadingFS = ref(false);

// Accordion states - all collapsed by default
const expandedIncomeGroups = ref(new Set());
const expandedExpenseGroups = ref(new Set());

const toggleGroup = (groupSet, groupName) => {
  // groupSet is already unwrapped in template, but we need the ref
  // So we check which ref it belongs to and update accordingly
  if (expandedIncomeGroups.value === groupSet) {
    if (groupSet.has(groupName)) {
      groupSet.delete(groupName);
    } else {
      groupSet.add(groupName);
    }
    expandedIncomeGroups.value = new Set(groupSet);
  } else if (expandedExpenseGroups.value === groupSet) {
    if (groupSet.has(groupName)) {
      groupSet.delete(groupName);
    } else {
      groupSet.add(groupName);
    }
    expandedExpenseGroups.value = new Set(groupSet);
  }
};

const reloadFinancialStatement = async () => {
  isLoadingFS.value = true;
  await store.loadFinancialStatement(store.dashboardData.customerId);
  isLoadingFS.value = false;
};
</script>

<template>
  <div class="balance-sheet-section">
    <AnLoader v-if="isLoadingFS" text="Financial Statement is loading..." />
    <template v-else>
      <!-- Balance Sheet Section -->
      <div class="section-header">
        <h2 class="section-title">Balance Sheet</h2>
      </div>

      <div class="balance-cards-grid">
        <!-- Assets Card -->
        <div class="balance-card assets-card">
          <div class="card-header">
            <div class="header-left">
              <i class="pi pi-chart-line"></i>
              <h3>Assets</h3>
            </div>
          </div>
          <div class="card-body">
            <div
              v-for="(asset, index) in filteredAssetData"
              :key="index"
              class="balance-item"
              :class="{ 'has-value': asset.assetValue > 0 }"
            >
              <div class="item-info">
                <span class="item-icon">💰</span>
                <span class="item-name">{{ asset.assetName }}</span>
              </div>
              <span class="item-value">{{ convertToInr(asset.assetValue) }}</span>
            </div>
            <div v-if="filteredAssetData.length === 0" class="empty-state">
              <i class="pi pi-inbox"></i>
              <p>No assets recorded</p>
            </div>
          </div>
          <div class="card-footer">
            <span class="footer-label">Total Assets</span>
            <span class="footer-value footer-value-income">{{ convertToInr(totalAsset) }}</span>
          </div>
          <div class="card-note">
            <i class="pi pi-info-circle"></i>
            <span>To update assets, please visit the <strong>My Portfolio</strong> tab</span>
          </div>
        </div>

        <!-- Liabilities Card -->
        <div class="balance-card liabilities-card">
          <div class="card-header">
            <div class="header-left">
              <i class="pi pi-exclamation-triangle"></i>
              <h3>Liabilities</h3>
            </div>
            <button class="action-btn" @click="showLiabilityDialog = true">
              <i class="pi pi-pencil"></i>
              Update
            </button>
          </div>
          <div class="card-body">
            <div
              v-for="(liability, index) in filteredLiabilityData"
              :key="index"
              class="balance-item"
            >
              <div class="item-info">
                <span class="item-icon">📋</span>
                <div class="item-details">
                  <span class="item-name">{{ liability.liabilityNm }}</span>
                  <span class="item-meta" v-if="liability.emiAmount">
                    EMI: {{ convertToInr(liability.emiAmount) }}/mo
                  </span>
                </div>
              </div>
              <span class="item-value item-value-negative">{{ convertToInr(liability.amtOutstanding) }}</span>
            </div>
            <div v-if="filteredLiabilityData.length === 0" class="empty-state">
              <i class="pi pi-check-circle"></i>
              <p>No liabilities</p>
            </div>
          </div>
          <div class="card-footer">
            <span class="footer-label">Total Liabilities</span>
            <span class="footer-value footer-value-negative">{{ convertToInr(totalLiability) }}</span>
          </div>
        </div>
      </div>

      <!-- Income & Expenses Section -->
      <div class="section-header">
        <h2 class="section-title">Income & Expenses</h2>
        <span class="section-subtitle">Monthly Breakdown</span>
      </div>

      <div class="income-expense-grid">
        <!-- Income Card -->
        <div class="ie-card income-card">
          <div class="ie-card-header">
            <div class="header-left">
              <i class="pi pi-arrow-down-left"></i>
              <h3>Income Sources</h3>
            </div>
            <button class="action-btn" @click="showIncomeDialog = true">
              <i class="pi pi-pencil"></i>
              Update
            </button>
          </div>
          <div class="ie-card-body">
            <template v-for="(group, groupName) in groupedIncome" :key="groupName">
              <div class="ie-group" v-if="group.filter(item => item.incomeAmt > 0).length > 0">
                <div
                  class="group-header group-header-clickable"
                  @click="toggleGroup(expandedIncomeGroups, groupName)"
                >
                  <div class="group-header-left">
                    <i class="pi" :class="expandedIncomeGroups.has(groupName) ? 'pi-chevron-down' : 'pi-chevron-right'"></i>
                    <span class="group-name">{{ groupName }}</span>
                  </div>
                  <span class="group-total">{{ convertToInr(groupTotals.income[groupName]) }}</span>
                </div>
                <div class="group-items" v-show="expandedIncomeGroups.has(groupName)">
                  <div
                    v-for="(item, index) in group.filter(item => item.incomeAmt > 0)"
                    :key="index"
                    class="ie-item"
                  >
                    <span class="ie-item-name">{{ item.incomeTyp }}</span>
                    <span class="ie-item-value ie-item-value-income">{{ convertToInr(item.incomeAmt) }}</span>
                  </div>
                </div>
              </div>
            </template>
            <div v-if="filteredIncomeData.length === 0" class="empty-state">
              <i class="pi pi-inbox"></i>
              <p>No income sources added</p>
            </div>
          </div>
          <div class="ie-card-footer">
            <span class="footer-label">Total Monthly Income</span>
            <span class="footer-value footer-value-income">{{ convertToInr(totalIncome) }}</span>
          </div>
        </div>

        <!-- Expenses Card -->
        <div class="ie-card expense-card">
          <div class="ie-card-header">
            <div class="header-left">
              <i class="pi pi-arrow-up-right"></i>
              <h3>Expenses</h3>
            </div>
            <button class="action-btn" @click="showExpenseDialog = true">
              <i class="pi pi-pencil"></i>
              Update
            </button>
          </div>
          <div class="ie-card-body">
            <template v-for="(group, groupName) in groupedExpenses" :key="groupName">
              <div class="ie-group" v-if="group.filter(item => item.expAmt > 0).length > 0">
                <div
                  class="group-header group-header-clickable"
                  @click="toggleGroup(expandedExpenseGroups, groupName)"
                >
                  <div class="group-header-left">
                    <i class="pi" :class="expandedExpenseGroups.has(groupName) ? 'pi-chevron-down' : 'pi-chevron-right'"></i>
                    <span class="group-name">{{ groupName }}</span>
                  </div>
                  <span class="group-total">{{ convertToInr(groupTotals.expenses[groupName]) }}</span>
                </div>
                <div class="group-items" v-show="expandedExpenseGroups.has(groupName)">
                  <div
                    v-for="(item, index) in group.filter(item => item.expAmt > 0)"
                    :key="index"
                    class="ie-item"
                  >
                    <span class="ie-item-name">{{ item.expType }}</span>
                    <span class="ie-item-value ie-item-value-expense">{{ convertToInr(item.expAmt) }}</span>
                  </div>
                </div>
              </div>
            </template>
            <div v-if="filteredExpenseData.length === 0" class="empty-state">
              <i class="pi pi-inbox"></i>
              <p>No expenses recorded</p>
            </div>
          </div>
          <div class="ie-card-footer">
            <span class="footer-label">Total Monthly Expenses</span>
            <span class="footer-value footer-value-expense">{{ convertToInr(totalExpense) }}</span>
          </div>
        </div>
      </div>

      <!-- Dialogs -->
      <ManageLiabilityDialog v-model="showLiabilityDialog" />
      <ManageIncomeDialog v-model="showIncomeDialog" />
      <ManageExpenseDialog v-model="showExpenseDialog" />
    </template>
  </div>
</template>

<style scoped>
.balance-sheet-section {
  margin-top: 30px;
}

/* Section Headers */
.section-header {
  margin-bottom: 20px;
}

.section-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 4px 0;
}

.section-subtitle {
  font-size: 0.875rem;
  color: #6b7280;
}

/* Balance Cards Grid */
.balance-cards-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  margin-bottom: 40px;
}

.balance-card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: linear-gradient(135deg, var(--an-primary, #0d7c68) 0%, #0a6454 100%);
  color: white;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.header-left i {
  font-size: 1.2rem;
}

.header-left h3 {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
}

.total-badge {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
}

.badge-label {
  font-size: 0.7rem;
  opacity: 0.9;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.badge-value {
  font-size: 1.1rem;
  font-weight: 700;
}

.action-btn {
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s ease;
}

.action-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: translateY(-1px);
}

.card-body {
  padding: 20px;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.balance-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  background: #f9fafb;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.balance-item.has-value {
  background: #ecfdf5;
  border-left: 3px solid #10b981;
}

.balance-item:hover {
  background: #f3f4f6;
  transform: translateX(2px);
}

.item-info {
  display: flex;
  align-items: center;
  gap: 10px;
}

.item-icon {
  font-size: 1.2rem;
}

.item-details {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.item-name {
  font-size: 0.9rem;
  font-weight: 500;
  color: #1f2937;
}

.item-meta {
  font-size: 0.75rem;
  color: #6b7280;
}

.item-value {
  font-size: 0.95rem;
  font-weight: 700;
  color: #059669;
}

.item-value-negative {
  color: #dc2626;
}

.card-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: #f9fafb;
  border-top: 1px solid #e5e7eb;
}

.footer-label {
  font-size: 0.9rem;
  font-weight: 600;
  color: #6b7280;
}

.footer-value {
  font-size: 1.1rem;
  font-weight: 700;
  color: #1f2937;
}

.footer-value-negative {
  color: #dc2626;
}

.footer-value-income {
  color: #059669;
}

.card-note {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  background: #eff6ff;
  border-top: 1px solid #dbeafe;
  font-size: 0.8rem;
  color: #1e40af;
}

.card-note i {
  font-size: 0.9rem;
  color: #3b82f6;
}

.card-note strong {
  font-weight: 600;
  color: #1e3a8a;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
  color: #9ca3af;
}

.empty-state i {
  font-size: 2.5rem;
  margin-bottom: 12px;
  opacity: 0.5;
}

.empty-state p {
  margin: 0;
  font-size: 0.9rem;
}

/* Income & Expense Grid */
.income-expense-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
}

.ie-card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.ie-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  color: white;
}

.income-card .ie-card-header {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
}

.expense-card .ie-card-header {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
}

.ie-card-body {
  padding: 20px;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-height: 400px;
  overflow-y: auto;
}

.ie-card-body::-webkit-scrollbar {
  width: 6px;
}

.ie-card-body::-webkit-scrollbar-track {
  background: #f3f4f6;
  border-radius: 3px;
}

.ie-card-body::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}

.ie-card-body::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

.ie-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.group-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  border-left: 3px solid #3b82f6;
  border-radius: 6px;
}

.group-header-clickable {
  cursor: pointer;
  transition: all 0.2s ease;
}

.group-header-clickable:hover {
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
  transform: translateX(2px);
}

.group-header-left {
  display: flex;
  align-items: center;
  gap: 8px;
}

.group-header-left i {
  font-size: 0.75rem;
  color: #3b82f6;
  transition: transform 0.2s ease;
}

.group-name {
  font-size: 0.875rem;
  font-weight: 700;
  color: #1e40af;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.group-total {
  font-size: 0.85rem;
  font-weight: 700;
  color: #3b82f6;
}

.group-items {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-left: 24px;
  margin-top: 8px;
}

.ie-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 16px;
  background: #f9fafb;
  border-radius: 6px;
  transition: all 0.2s ease;
}

.ie-item:hover {
  background: #f3f4f6;
  transform: translateX(2px);
}

.ie-item-name {
  font-size: 0.875rem;
  color: #4b5563;
  font-weight: 500;
}

.ie-item-value {
  font-size: 0.9rem;
  font-weight: 700;
}

.ie-item-value-income {
  color: #059669;
}

.ie-item-value-expense {
  color: #dc2626;
}

.ie-card-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-top: 2px solid #e5e7eb;
  background: #f9fafb;
}

.ie-card-footer .footer-label {
  font-size: 0.95rem;
  font-weight: 600;
  color: #374151;
}

.ie-card-footer .footer-value {
  font-size: 1.25rem;
  font-weight: 700;
}

.footer-value-income {
  color: #059669;
}

.footer-value-expense {
  color: #dc2626;
}

/* Responsive Design */
@media (max-width: 1024px) {
  .balance-cards-grid,
  .income-expense-grid {
    grid-template-columns: 1fr;
  }

  .ie-card-body {
    max-height: 300px;
  }
}

@media (max-width: 768px) {
  .section-title {
    font-size: 1.1rem;
  }

  .card-header,
  .ie-card-header {
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;
  }

  .total-badge {
    align-items: flex-start;
  }

  .action-btn {
    align-self: stretch;
    justify-content: center;
  }

  .ie-card-body {
    max-height: none;
  }
}
</style>
